/**
 * DigestProfilesPage — Phase 5: Multi-Digest Profiles manager
 * Behind ENABLE_MULTI_DIGEST_PROFILES
 * Phase UX-E: When ENABLE_PREFERENCES_WIZARD_V2=true, shows unified wizard for editing
 *
 * Allows Premium / Trial-active users to manage up to 5 literature queues.
 * Free users are limited to 1.
 * PHI-Zero: no user-entered text is logged.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { preferencesAPI, configAPI } from '../api';
import { Button } from '../components/ui/button';
import PracticeProfileEditor from '../components/PracticeProfileEditor';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Edit2, Trash2, BookOpen, Clock, Loader2,
  CheckCircle2, AlertCircle, X, FileText, Settings, Star, Play
} from 'lucide-react';
import { BACKEND_URL } from 'apiConfig';
import UnifiedPreferencesWizard from '../components/UnifiedPreferencesWizard';

// ── Profile form (create / edit) ───────────────────────────────────────────

// Frequency dropdown options (same as onboarding wizard)
const INTERVAL_VALUES = [1, 2, 3, 4, 5, 6, 7];
const INTERVAL_UNITS = [
  { value: 'days', label: 'day(s)' },
  { value: 'weeks', label: 'week(s)' },
];
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const defaultForm = () => ({
  name: '',
  specialty_id: '',
  subspecialty_id: '',
  custom_keywords: '',  // comma-separated for input
  journals_selected: [],  // selected journal names
  schedule: { 
    frequency: 'daily', 
    hour: 9, 
    minute: 0,
    intervalValue: 1,
    intervalUnit: 'days',
    dayOfWeek: 'Monday',
  },
});

const ProfileForm = ({ specialties, initial, onSave, onCancel, saving, isPremiumOrTrial }) => {
  const [form, setForm] = useState(() => {
    if (!initial) return defaultForm();
    
    // Parse existing frequency to intervalValue/intervalUnit
    let intervalValue = 1;
    let intervalUnit = 'days';
    let dayOfWeek = 'Monday';
    const freq = initial.digest_frequency || 'daily';
    if (freq === 'daily') {
      intervalValue = 1;
      intervalUnit = 'days';
    } else if (freq === 'weekly') {
      intervalValue = 1;
      intervalUnit = 'weeks';
    } else if (freq === 'biweekly') {
      intervalValue = 2;
      intervalUnit = 'weeks';
    } else if (freq === 'monthly') {
      intervalValue = 4;
      intervalUnit = 'weeks';
    }
    if (initial.day_of_week) {
      dayOfWeek = initial.day_of_week;
    }
    
    return {
      name: initial.name || '',
      specialty_id: initial.specialty_id || '',
      subspecialty_id: initial.subspecialty_id || '',
      custom_keywords: (initial.custom_keywords || []).join(', '),
      journals_selected: initial.journals_selected || [],
      schedule: {
        frequency: freq,
        hour: initial.delivery_time ? parseInt(initial.delivery_time.split(':')[0]) : 9,
        minute: initial.delivery_time ? parseInt(initial.delivery_time.split(':')[1]) : 0,
        intervalValue,
        intervalUnit,
        dayOfWeek,
      },
    };
  });

  const selectedSpecialty = specialties?.find(s => s.id === form.specialty_id);
  const subspecialties = selectedSpecialty?.subspecialties || [];
  
  // Compute effective frequency from interval settings
  const getEffectiveFrequency = () => {
    const { intervalValue, intervalUnit } = form.schedule;
    if (intervalUnit === 'days' && intervalValue === 1) return 'daily';
    if (intervalUnit === 'weeks' && intervalValue === 1) return 'weekly';
    if (intervalUnit === 'weeks' && intervalValue === 2) return 'biweekly';
    return `every_${intervalValue}_${intervalUnit}`;
  };

  // Derive available journals: subspecialty selected = top 10 subspec + top 5 spec; specialty only = top 15
  const availableJournals = (() => {
    if (!selectedSpecialty) return [];
    const subs = selectedSpecialty.subspecialties || [];
    if (form.subspecialty_id) {
      const sub = subs.find(s => s.id === form.subspecialty_id);
      const subJournals = (sub?.top_journals || []).slice(0, 10);
      // Add top 5 specialty-level journals (from all subspecialties, excluding already listed)
      const subSet = new Set(subJournals);
      const specJournals = [];
      for (const s of subs) {
        for (const j of (s.top_journals || [])) {
          if (!subSet.has(j) && !specJournals.includes(j)) {
            specJournals.push(j);
            if (specJournals.length >= 5) break;
          }
        }
        if (specJournals.length >= 5) break;
      }
      return [...subJournals, ...specJournals];
    }
    // No subspecialty selected — collect from all subspecialties, deduplicate, limit to 15
    const all = new Set();
    subs.forEach(s => (s.top_journals || []).forEach(j => all.add(j)));
    return Array.from(all).sort().slice(0, 15);
  })();

  const toggleJournal = (journal) => {
    setForm(f => {
      const current = f.journals_selected || [];
      if (current.includes(journal)) {
        return { ...f, journals_selected: current.filter(j => j !== journal) };
      }
      return { ...f, journals_selected: [...current, journal] };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.specialty_id) {
      toast.error('Name and specialty are required');
      return;
    }
    
    // Auto-enroll in community based on specialty/subspecialty selection
    const communityIds = [];
    if (form.subspecialty_id) {
      communityIds.push(form.subspecialty_id);
    } else {
      // If no subspecialty, enroll in the core/general community
      communityIds.push(`${form.specialty_id}_core`);
    }
    
    onSave({
      name: form.name.trim(),
      specialty_id: form.specialty_id,
      subspecialty_id: form.subspecialty_id || null,
      custom_keywords: form.custom_keywords
        .split(',')
        .map(k => k.trim())
        .filter(Boolean),
      journals_selected: form.journals_selected || [],
      community_subspecialty_ids: communityIds,
      schedule: {
        ...form.schedule,
        frequency: getEffectiveFrequency(),
      },
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label htmlFor="pf-name" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Profile name</Label>
          <Input
            id="pf-name"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Cardiology – HF"
            className="mt-1"
            maxLength={80}
            required
            data-testid="profile-name-input"
          />
        </div>
        <div>
          <Label htmlFor="pf-spec" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Specialty</Label>
          <select
            id="pf-spec"
            value={form.specialty_id}
            onChange={e => setForm(f => ({ ...f, specialty_id: e.target.value, subspecialty_id: '' }))}
            className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            required
            data-testid="profile-specialty-select"
          >
            <option value="">Select specialty…</option>
            {(specialties || []).map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="pf-sub" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Subspecialty <span className="text-slate-400 normal-case">(optional)</span></Label>
          <select
            id="pf-sub"
            value={form.subspecialty_id}
            onChange={e => setForm(f => ({ ...f, subspecialty_id: e.target.value }))}
            className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
            disabled={subspecialties.length === 0}
            data-testid="profile-subspecialty-select"
          >
            <option value="">Any subspecialty</option>
            {subspecialties.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
        
        {/* Journal Selection */}
        {availableJournals.length > 0 && (
          <div className="sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Preferred Journals <span className="text-slate-400 normal-case">({(form.journals_selected || []).length} selected)</span>
            </Label>
            <p className="text-xs text-slate-500 mt-0.5 mb-2">
              Select journals to prioritize in your digest. Articles from these journals are ranked higher.
            </p>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto p-2 border border-slate-200 rounded-lg bg-slate-50/50" data-testid="journal-picker">
              {availableJournals.map(journal => {
                const isSelected = (form.journals_selected || []).includes(journal);
                return (
                  <button
                    key={journal}
                    type="button"
                    onClick={() => toggleJournal(journal)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                      isSelected
                        ? 'bg-sky-600 border-sky-600 text-white'
                        : 'border-slate-200 text-slate-600 hover:border-slate-400 bg-white'
                    }`}
                    data-testid={`journal-${journal.replace(/\s/g, '-')}`}
                  >
                    {journal}
                  </button>
                );
              })}
            </div>
            {(form.journals_selected || []).length > 0 && (
              <button
                type="button"
                onClick={() => setForm(f => ({ ...f, journals_selected: [] }))}
                className="text-xs text-slate-400 hover:text-slate-600 mt-1"
              >
                Clear all
              </button>
            )}
          </div>
        )}

        <div className="sm:col-span-2">
          <Label htmlFor="pf-kw" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Additional keywords <span className="text-slate-400 normal-case">(comma-separated)</span></Label>
          <Input
            id="pf-kw"
            value={form.custom_keywords}
            onChange={e => setForm(f => ({ ...f, custom_keywords: e.target.value }))}
            placeholder="e.g. SGLT2 inhibitors, HFrEF, sacubitril"
            className="mt-1"
            data-testid="profile-keywords-input"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Frequency</Label>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-slate-600">Every</span>
            <select
              value={form.schedule.intervalValue}
              onChange={e => setForm(p => ({ 
                ...p, 
                schedule: { ...p.schedule, intervalValue: parseInt(e.target.value) } 
              }))}
              className="w-20 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="interval-value-select"
            >
              {INTERVAL_VALUES.map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <select
              value={form.schedule.intervalUnit}
              onChange={e => setForm(p => ({ 
                ...p, 
                schedule: { ...p.schedule, intervalUnit: e.target.value } 
              }))}
              className="w-28 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="interval-unit-select"
            >
              {INTERVAL_UNITS.map(u => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            {getEffectiveFrequency() === 'daily' && 'You will receive a digest every day.'}
            {getEffectiveFrequency() === 'weekly' && 'You will receive a digest once a week.'}
            {getEffectiveFrequency() === 'biweekly' && 'You will receive a digest every two weeks.'}
            {!['daily', 'weekly', 'biweekly'].includes(getEffectiveFrequency()) && 
              `You will receive a digest every ${form.schedule.intervalValue} ${form.schedule.intervalUnit}.`}
          </p>
        </div>
        
        {/* Day of Week (for weekly intervals) */}
        {form.schedule.intervalUnit === 'weeks' && (
          <div>
            <Label className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Preferred Day</Label>
            <select
              value={form.schedule.dayOfWeek}
              onChange={e => setForm(p => ({ 
                ...p, 
                schedule: { ...p.schedule, dayOfWeek: e.target.value } 
              }))}
              className="w-full mt-1 h-10 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="day-of-week-select"
            >
              {DAYS_OF_WEEK.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <Label htmlFor="pf-hour" className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Delivery time (24h)</Label>
          <div className="flex gap-2 mt-1 items-center">
            <Input
              id="pf-hour"
              type="number"
              min={0} max={23}
              value={form.schedule.hour}
              onChange={e => setForm(p => ({ ...p, schedule: { ...p.schedule, hour: parseInt(e.target.value) || 0 } }))}
              className="w-20"
            />
            <span className="text-slate-500 text-sm">:</span>
            <Input
              type="number"
              min={0} max={59} step={15}
              value={form.schedule.minute}
              onChange={e => setForm(p => ({ ...p, schedule: { ...p.schedule, minute: parseInt(e.target.value) || 0 } }))}
              className="w-20"
            />
          </div>
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <Button type="submit" disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="profile-save-btn">
          {saving ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Saving…</> : <><CheckCircle2 className="h-4 w-4 mr-1.5" />Save profile</>}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="profile-cancel-btn">
          <X className="h-4 w-4 mr-1.5" />Cancel
        </Button>
      </div>
    </form>
  );
};

// ── Profile card ───────────────────────────────────────────────────────────

const ProfileCard = ({ profile, specialties, onEdit, onDelete, onSetPrimary, onRunDigest, deleting }) => {
  const spec = specialties?.find(s => s.id === profile.specialty_id);
  const sub = spec?.subspecialties?.find(s => s.id === profile.subspecialty_id);
  const isFrozen = !profile.is_active && profile.frozen_reason;

  // Phase UX-D: Show additional info if profile has full wizard fields
  const hasTopics = profile.topics_selected?.length > 0 || profile.custom_topics?.length > 0;
  const hasJournals = profile.journals_selected?.length > 0 || profile.custom_journals?.length > 0;
  const topicsCount = (profile.topics_selected?.length || 0) + (profile.custom_topics?.length || 0);
  const journalsCount = (profile.journals_selected?.length || 0) + (profile.custom_journals?.length || 0);

  return (
    <Card
      className={`border transition-colors ${isFrozen ? 'border-slate-200 bg-slate-50/50 opacity-75' : 'border-slate-200 hover:border-slate-300 cursor-pointer'}`}
      data-testid={`profile-card-${profile.profile_id}`}
      onClick={() => !isFrozen && onEdit(profile)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <BookOpen className={`h-4 w-4 shrink-0 ${isFrozen ? 'text-slate-400' : 'text-sky-500'}`} />
              <span className={`font-semibold truncate ${isFrozen ? 'text-slate-500' : 'text-slate-900'}`} data-testid={`profile-name-${profile.profile_id}`}>
                {profile.name}
              </span>
              {/* Badges */}
              {profile.is_primary && (
                <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-700 border-amber-300 ml-1">
                  <Star className="h-2.5 w-2.5 mr-0.5" />primary
                </Badge>
              )}
              {profile.is_active && (
                <Badge variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200 ml-1">
                  Active
                </Badge>
              )}
              {isFrozen && (
                <Badge variant="secondary" className="text-[10px] bg-blue-100 text-blue-600 border-blue-200 ml-1" title="Upgrade or swap an active queue to edit.">
                  Frozen
                </Badge>
              )}
              {!profile.is_active && !isFrozen && (
                <Badge variant="outline" className="text-[10px] text-slate-500 ml-1">paused</Badge>
              )}
            </div>
            <div className={`text-sm ml-6 ${isFrozen ? 'text-slate-400' : 'text-slate-500'}`}>
              {spec?.label || profile.specialty_id}
              {sub && <> · {sub.label}</>}
            </div>
            {(hasTopics || hasJournals) && (
              <div className="flex items-center gap-3 mt-2 ml-6 text-xs text-slate-400">
                {hasTopics && (
                  <span className="flex items-center gap-1">
                    <FileText className="h-3 w-3" />
                    {topicsCount} topic{topicsCount !== 1 ? 's' : ''}
                  </span>
                )}
                {hasJournals && (
                  <span className="flex items-center gap-1">
                    <BookOpen className="h-3 w-3" />
                    {journalsCount} journal{journalsCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
            {profile.custom_keywords?.length > 0 && !hasTopics && (
              <div className="flex flex-wrap gap-1 mt-2 ml-6">
                {profile.custom_keywords.slice(0, 4).map((kw, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px] bg-slate-100 text-slate-600">{kw}</Badge>
                ))}
                {profile.custom_keywords.length > 4 && (
                  <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-500">+{profile.custom_keywords.length - 4}</Badge>
                )}
              </div>
            )}
            <div className="flex items-center gap-1.5 mt-2 ml-6 text-xs text-slate-400">
              <Clock className="h-3 w-3" />
              <span className="capitalize">{profile.digest_frequency}</span>
              {profile.delivery_time && <span>at {profile.delivery_time}</span>}
              {profile.max_articles_per_digest && profile.max_articles_per_digest !== 10 && (
                <span>· {profile.max_articles_per_digest} articles</span>
              )}
            </div>
            {isFrozen && (
              <p className="text-xs text-blue-500 mt-2 ml-6">
                Frozen — upgrade or swap an active queue to edit.
              </p>
            )}
          </div>
          {/* Actions — hidden for frozen profiles */}
          {!isFrozen && (
            <div className="flex gap-1 shrink-0">
              <Button
                variant="ghost" size="sm"
                className="h-8 w-8 p-0 text-slate-400 hover:text-emerald-600"
                onClick={(e) => { e.stopPropagation(); onRunDigest && onRunDigest(profile); }}
                data-testid={`profile-run-btn-${profile.profile_id}`}
                aria-label={`Run queue for ${profile.name}`} title="Run Queue Now"
              >
                <Play className="h-3.5 w-3.5" />
              </Button>
              {!profile.is_primary && (
                <Button
                  variant="ghost" size="sm"
                  className="h-8 w-8 p-0 text-slate-400 hover:text-amber-600"
                  onClick={(e) => { e.stopPropagation(); onSetPrimary(profile); }}
                  data-testid={`profile-primary-btn-${profile.profile_id}`}
                  aria-label={`Set ${profile.name} as primary`} title="Set as primary"
                >
                  <Star className="h-3.5 w-3.5" />
                </Button>
              )}
              <Button
                variant="ghost" size="sm"
                className="h-8 w-8 p-0 text-slate-400 hover:text-sky-600"
                onClick={(e) => { e.stopPropagation(); onEdit(profile); }}
                data-testid={`profile-edit-btn-${profile.profile_id}`}
                aria-label={`Edit ${profile.name}`}
              >
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost" size="sm"
                className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                onClick={(e) => { e.stopPropagation(); onDelete(profile); }}
                disabled={deleting}
                data-testid={`profile-delete-btn-${profile.profile_id}`}
                aria-label={`Delete ${profile.name}`}
              >
                {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Main page ──────────────────────────────────────────────────────────────

const DigestProfilesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { flags } = useFeatureFlags();

  const [profiles, setProfiles] = useState([]);
  const [maxProfiles, setMaxProfiles] = useState(1);
  const [atLimit, setAtLimit] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Phase UX-D: Full wizard mode
  const [showWizard, setShowWizard] = useState(false);
  const [wizardProfile, setWizardProfile] = useState(null);
  // Batch 4: Manage active digests selector
  const [showManageActive, setShowManageActive] = useState(false);
  const [selectedActiveIds, setSelectedActiveIds] = useState([]);
  const [savingActive, setSavingActive] = useState(false);

  // Phase UX-E: Check if unified wizard is enabled (takes precedence over old wizard)
  const unifiedWizardEnabled = flags.enable_preferences_wizard_v2 === true;
  // Phase UX-D: Check if old full wizard is enabled (fallback if unified not enabled)
  const fullWizardEnabled = flags.enable_digest_profile_full_wizard === true;
  // Use unified wizard if enabled, otherwise use old wizard if enabled
  const useWizard = true; // Always use UnifiedPreferencesWizard for consistent queue creation

  useEffect(() => {
    // Only load data if the multi-literature queues feature is enabled
    if (flags.enable_multi_digest_profiles) {
      loadData();
    } else {
      setLoading(false);
      toast.error('Literature Queues are not currently enabled.');
      navigate('/preferences');
    }
  }, [flags.enable_multi_digest_profiles]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profilesResp, configResp] = await Promise.all([
        preferencesAPI.listProfiles(),
        configAPI.getSpecialties().catch(() => ({ specialties: [] })),
      ]);
      setProfiles(profilesResp.profiles || []);
      setMaxProfiles(profilesResp.max_profiles || 1);
      setAtLimit(profilesResp.at_limit || false);
      setSpecialties(configResp.specialties || []);
    } catch (err) {
      const code = err?.response?.data?.detail?.error_code;
      if (code === 'feature_disabled') {
        toast.error('Literature Queues are not enabled.');
        navigate('/preferences');
      } else {
        toast.error('Failed to load profiles');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    setSaving(true);
    try {
      const created = await preferencesAPI.createProfile(formData);
      setProfiles(prev => [...prev, created]);
      setAtLimit(profiles.length + 1 >= maxProfiles);
      setShowForm(false);
      toast.success('Profile created');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error_code === 'profile_limit_reached') {
        toast.error(detail.message);
      } else if (detail?.error_code === 'phi_detected') {
        toast.error('Please remove any patient identifiers (names, DOB, SSN, etc.) from your profile.');
      } else {
        toast.error('Failed to create profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (formData) => {
    if (!editingProfile) return;
    setSaving(true);
    try {
      const updated = await preferencesAPI.updateProfile(editingProfile.profile_id, formData);
      setProfiles(prev => prev.map(p => p.profile_id === updated.profile_id ? updated : p));
      setEditingProfile(null);
      toast.success('Profile updated');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error_code === 'phi_detected') {
        toast.error('Please remove any patient identifiers (names, DOB, SSN, etc.) from your profile.');
      } else {
        toast.error('Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`Delete this queue? Its articles will be hidden.`)) return;
    setDeletingId(profile.profile_id);
    try {
      await preferencesAPI.deleteProfile(profile.profile_id);
      setProfiles(prev => prev.filter(p => p.profile_id !== profile.profile_id));
      setAtLimit(false);
      toast.success(`"${profile.name}" deleted`);
    } catch (err) {
      const code = err?.response?.data?.detail?.error_code;
      if (code === 'cannot_delete_last_profile') {
        toast.error('You must keep at least one literature queue.');
      } else {
        toast.error('Failed to delete profile');
      }
    } finally {
      setDeletingId(null);
    }
  };

  const handleSetPrimary = async (profile) => {
    try {
      await preferencesAPI.updateProfile(profile.profile_id, { is_primary: true });
      setProfiles(prev => prev.map(p => ({
        ...p,
        is_primary: p.profile_id === profile.profile_id,
      })));
      toast.success(`"${profile.name}" set as primary`);
    } catch (err) {
      toast.error('Failed to set primary profile');
    }
  };

  const handleRunDigest = async (profile) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/preferences/profiles/${profile.profile_id}/run-digest`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to run digest');
      }
      
      toast.success(`Running queue for "${profile.name}". New articles will appear shortly.`);
    } catch (err) {
      toast.error('Failed to run queue');
    }
  };

  const handleSaveActiveSelection = async () => {
    if (selectedActiveIds.length === 0) { toast.error('Select at least 1 profile'); return; }
    if (selectedActiveIds.length > maxProfiles) { toast.error(`Maximum ${maxProfiles} active profiles allowed`); return; }
    setSavingActive(true);
    try {
      await preferencesAPI.setActiveProfiles(selectedActiveIds);
      toast.success(`${selectedActiveIds.length} profile(s) activated`);
      setShowManageActive(false);
      await loadData();
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || 'Failed to update active profiles';
      toast.error(msg);
    } finally {
      setSavingActive(false);
    }
  };

  const frozenProfiles = profiles.filter(p => !p.is_active && p.frozen_reason);
  const activeProfiles = profiles.filter(p => p.is_active);
  const hasFrozenProfiles = frozenProfiles.length > 0;

  const isPremium = user?.plan_tier === 'premium' || user?.trial_active;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50/50 via-slate-50 to-white" data-testid="digest-profiles-page">
      <div className="bg-white border-b">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
          <Button variant="ghost" size="sm" onClick={() => navigate('/home')} className="mb-3 -ml-2">
            <ArrowLeft className="h-4 w-4 mr-1.5" />Back
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Literature Queues</h1>
              <p className="text-sm text-slate-500 mt-0.5">Each queue generates articles on its own schedule.</p>
            </div>
            {!loading && (
              <div className="flex items-center gap-2">
                {hasFrozenProfiles && (
                  <Button size="sm" variant="outline" className="text-xs"
                    onClick={() => { setSelectedActiveIds(activeProfiles.map(p => p.profile_id)); setShowManageActive(true); }}>
                    Manage Active
                  </Button>
                )}
                <Badge
                  className={`text-sm px-3 py-1 ${isPremium ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-slate-100 text-slate-600'}`}
                  variant="outline"
                  data-testid="profiles-count-badge"
                >
                  {activeProfiles.length} active / {maxProfiles} max
                </Badge>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
          </div>
        ) : showManageActive ? (
          /* ---- Manage Active Digests Selector ---- */
          <Card className="border-sky-200">
            <CardHeader>
              <CardTitle className="text-base">Select Active Digests</CardTitle>
              <p className="text-sm text-slate-500">
                Choose up to {maxProfiles} profile{maxProfiles !== 1 ? 's' : ''} to keep active. Others will be frozen (view-only).
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {profiles.map(p => {
                const spec = specialties?.find(s => s.id === p.specialty_id);
                const isSelected = selectedActiveIds.includes(p.profile_id);
                const atMax = selectedActiveIds.length >= maxProfiles && !isSelected;
                return (
                  <label
                    key={p.profile_id}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      isSelected ? 'border-sky-300 bg-sky-50' : atMax ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={atMax}
                      onChange={() => {
                        setSelectedActiveIds(prev =>
                          prev.includes(p.profile_id)
                            ? prev.filter(id => id !== p.profile_id)
                            : [...prev, p.profile_id]
                        );
                      }}
                      className="rounded border-slate-300"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-sm text-slate-800">{p.name}</span>
                      <span className="text-xs text-slate-500 ml-2">{spec?.label || p.specialty_id}</span>
                      {p.is_primary && <Badge variant="secondary" className="text-[9px] bg-amber-100 text-amber-700 ml-2">primary</Badge>}
                    </div>
                  </label>
                );
              })}
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowManageActive(false)} className="flex-1">Cancel</Button>
                <Button
                  onClick={handleSaveActiveSelection}
                  disabled={savingActive || selectedActiveIds.length === 0}
                  className="flex-1 bg-sky-600 hover:bg-sky-700 text-white"
                >
                  {savingActive ? 'Saving...' : `Activate ${selectedActiveIds.length} / ${maxProfiles}`}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : showWizard ? (
          /* Phase UX-E: Unified wizard for all profile editing */
            <UnifiedPreferencesWizard
              mode="edit"
              profile={wizardProfile}
              onComplete={() => {
                setShowWizard(false);
                setWizardProfile(null);
                loadData();
              }}
              onCancel={() => {
                setShowWizard(false);
                setWizardProfile(null);
              }}
              isPremiumOrTrial={isPremium}
            />
        ) : (
          <>
            {/* Frozen profiles banner */}
            {hasFrozenProfiles && !isPremium && (
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
                <p className="text-sm font-medium text-blue-900">
                  Your trial ended. Your account now supports <strong>{maxProfiles} active digest{maxProfiles !== 1 ? 's' : ''}</strong>. {frozenProfiles.length} profile{frozenProfiles.length !== 1 ? 's are' : ' is'} frozen (view-only).
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={() => { setSelectedActiveIds(activeProfiles.map(p => p.profile_id)); setShowManageActive(true); }}>
                    Manage active digests
                  </Button>
                  {!user?.peer_verification_status || (user.peer_verification_status !== 'verified' && user.peer_verification_status !== 'verified_provisional') ? (
                    <Button size="sm" variant="outline" className="text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => navigate('/verification')}>
                      Verify clinician status (unlock 3)
                    </Button>
                  ) : null}
                  <Button size="sm" variant="outline" className="text-xs border-sky-300 text-sky-700 hover:bg-sky-50"
                    onClick={() => navigate('/plan')}>
                    Upgrade to Pro (unlock 5)
                  </Button>
                </div>
              </div>
            )}

            {/* Profiles list */}
            <div className="space-y-3" data-testid="profiles-list">
              {profiles.map(profile => (
                editingProfile?.profile_id === profile.profile_id ? (
                  <Card key={profile.profile_id} className="border-sky-300 bg-sky-50/30">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Edit profile</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ProfileForm
                        specialties={specialties}
                        initial={editingProfile}
                        onSave={handleUpdate}
                        onCancel={() => setEditingProfile(null)}
                        saving={saving}
                        isPremiumOrTrial={isPremium}
                      />
                    </CardContent>
                  </Card>
                ) : (
                  <ProfileCard
                    key={profile.profile_id}
                    profile={profile}
                    specialties={specialties}
                    onEdit={p => {
                      // Phase UX-E: Use wizard if enabled (unified or old)
                      if (useWizard) {
                        setWizardProfile(p);
                        setShowWizard(true);
                      } else {
                        setEditingProfile(p);
                        setShowForm(false);
                      }
                    }}
                    onDelete={handleDelete}
                    onSetPrimary={handleSetPrimary}
                    onRunDigest={handleRunDigest}
                    deleting={deletingId === profile.profile_id}
                  />
                )
              ))}
            </div>

            {/* Add new profile / inline form */}
            {showForm && !editingProfile && (
              <Card className="border-sky-300 bg-sky-50/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">New profile</CardTitle>
                </CardHeader>
                <CardContent>
                  <ProfileForm
                    specialties={specialties}
                    initial={null}
                    onSave={handleCreate}
                    onCancel={() => setShowForm(false)}
                    saving={saving}
                    isPremiumOrTrial={isPremium}
                  />
                </CardContent>
              </Card>
            )}

            {/* Add button / limit message */}
            {!showForm && !editingProfile && (
              atLimit ? (
                <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="profiles-at-limit">
                  <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      {isPremium
                        ? `You've reached the maximum of ${maxProfiles} literature queues.`
                        : 'Free plan: 1 literature queue.'}
                    </p>
                    <p className="text-sm text-amber-700 mt-0.5">
                      Need more than {maxProfiles}? Email{' '}
                      <a href="mailto:info@scienthesis.ai" className="underline hover:text-amber-900">
                        info@scienthesis.ai
                      </a>
                    </p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => {
                    // Phase UX-E: Use wizard if enabled
                    if (useWizard) {
                      setWizardProfile(null);
                      setShowWizard(true);
                    } else {
                      setShowForm(true);
                    }
                  }}
                  className="w-full p-4 rounded-xl border-2 border-dashed border-sky-400 bg-sky-50/50 hover:bg-sky-100/70 hover:border-sky-500 transition-all group"
                  data-testid="add-profile-btn"
                >
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-sky-100 group-hover:bg-sky-200 flex items-center justify-center transition-colors">
                      <Plus className="h-5 w-5 text-sky-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-sky-700 group-hover:text-sky-800">
                        Add Another Literature Queue
                      </p>
                      <p className="text-xs text-sky-600/80">
                        {profiles.length} of {maxProfiles} queues used
                      </p>
                    </div>
                  </div>
                </button>
              )
            )}

            {/* Upgrade hint for free users */}
            {!isPremium && !atLimit && (
              <p className="text-xs text-slate-400 text-center">
                Upgrade to Pro for up to 5 literature queues.{' '}
                <button className="underline hover:text-slate-600" onClick={() => navigate('/plan')}>View plans</button>
              </p>
            )}
          </>
        )}

        {/* Practice Profile Editor */}
        <div className="mt-6">
          <PracticeProfileEditor />
        </div>
      </div>
    </div>
  );
};

export default DigestProfilesPage;
